import { IResponsePinCheck, IResponseUniquePhone, IResponseUser } from '@/core/domain/entities/user';
import axios from 'axios';
import {
  ICompleteCheckSmsReqParams,
  IJoinRequestParams,
  ILoginReqParams,
  IPinCheckReqParams,
} from '@/core/domain/entities/request';
import { mapPinCheck, mapUniqueNumber, mapUser } from '@/core/domain/mapper/mapUser';
import { getStores } from '@/stores';
import { checklocal } from '@/utils';

// 세션 체크 함수 추가
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function checkSession(): Promise<{ isLoggedIn: boolean; user?: any }> {
  try {
    const response = await axios.get(`${checklocal()}/member/check-session`, {
      withCredentials: true,
    });
    return response.data;
  } catch (err) {
    console.error('Session check failed:', err);
    return { isLoggedIn: false };
  }
}

/**
 * 로그인 API
 *
 * @export
 * @param {ILoginReqParams} loginData
 * @return {*}  {(Promise<number | IUser>)}
 */

export async function userLogin(
  loginData: ILoginReqParams,
): Promise<{ errorCode: string; errorCount?: number } | IResponseUser> {
  try {
    const { userStore } = getStores();
    const response = await axios.post(`${checklocal()}/member/sign`, loginData, {
      withCredentials: true,
    });

    if (
      response.data.errorCode.code === '1200' ||
      response.data.errorCode.code === '1201' ||
      response.data.errorCode.code === '1202' ||
      response.data.errorCode.code === '1203' ||
      response.data.errorCode.code === '1205' ||
      response.data.errorCode.code === '1208' ||
      response.data.errorCode.code === '1300'
    ) {
      return { errorCode: response.data.errorCode.code, errorCount: response.data.errorCount };
    }

    if (response.data.result[0].trx_address === undefined) {
      userStore.setUser(response.data.result[0]);

      const res = await createAddress(userStore.user?.id ?? '', loginData.phone);
      response.data.result[0].trx_address = res;

      // 주소 생성 후 로그인 재시도
      return userLogin(loginData); // 로그인 재시도
    }

    // 로그인 성공 시 세션 체크를 즉시 수행
    const sessionResponse = await checkSession();
    if (sessionResponse.isLoggedIn) {
      // 여기서 전역 상태 업데이트 함수를 호출
      return mapUser(sessionResponse.user);
    }

    return mapUser(response.data.result[0]);
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      const errorData = err.response?.data.error;

      if (errorData?.errorCount > 0) {
        return {
          errorCode: errorData.errorCode.code,
          errorCount: errorData.errorCount,
        };
      }
      return {
        errorCode: errorData?.errorCode?.code,
      };
    }
    return { errorCode: '' };
  }
}

/**
 * 휴대폰 번호 중복 체크 API
 *
 * @export
 * @param {string} phoneNumber
 * @return {*}  {Promise<boolean>}
 */
export async function validatePhoneNumberUniqueness(phone: string): Promise<string | IResponseUniquePhone> {
  try {
    const response = await axios.post(`${checklocal()}/member/checkPhoneUnique`, { phone: phone }, { timeout: 10000 });

    return mapUniqueNumber(response.data.isUnique);
  } catch (err) {
    console.error('Full error:', err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }
    return '0000';
  }
}

/**
 *
 *
 * @export
 * @param {ICompleteCheckSmsReqParams} reqData
 * @return {*}  {Promise<string>}
 */
export async function completeCheckSms(reqData: ICompleteCheckSmsReqParams): Promise<string> {
  try {
    const response = await axios.post(`${checklocal()}/member/sms/verify`, reqData);

    return response.data;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

export async function getCountry(): Promise<string> {
  try {
    const response = await axios.get(`${checklocal()}/member/country`);

    return response.data;
  } catch (err) {
    console.error(err);
    return '';
  }
}

export async function validateNicknameUniqueness(name: string): Promise<string> {
  try {
    const response = await axios.post(`${checklocal()}/member/checkNameUnique`, { name }, { timeout: 10000 });

    return response.data;
  } catch (err) {
    console.error(JSON.stringify(err));
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

export async function join(reqData: IJoinRequestParams): Promise<string> {
  try {
    const response = await axios.post(`${checklocal()}/member/join`, reqData);

    return response.data;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

export async function pinCheck(reqData: IPinCheckReqParams): Promise<string | IResponsePinCheck> {
  try {
    const response = await axios.post(`${checklocal()}/member/verifypin`, reqData);

    return mapPinCheck(response.data.result);
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

export async function logout(): Promise<string> {
  try {
    const response = await axios.post(
      `${checklocal()}/member/logout`,
      {}, // 빈 객체를 body로 전달
      { withCredentials: true }, // 세션 쿠키를 포함하기 위해 추가
    );

    return response.data.message; // 성공 메시지 반환
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.error || '로그아웃 실패';
    }
    return '로그아웃 실패';
  }
}

export async function logoutAll(): Promise<string> {
  try {
    const response = await axios.post(
      `${checklocal()}/member/logoutAll`,
      {}, // 빈 객체를 body로 전달
      { withCredentials: true }, // 세션 쿠키를 포함하기 위해 추가
    );

    return response.data.message; // 성공 메시지 반환
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.error || '로그아웃 실패';
    }
    return '로그아웃 실패';
  }
}

export async function createAddress(id: string, phone: string): Promise<string> {
  try {
    const response = await axios.post(
      `${checklocal()}/wallet/trx/createaddress`,
      { id, phone },
      // { withCredentials: true }, // 세션 쿠키를 포함하기 위해 추가
    );

    return response.data.result;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.error || '주소 생성 실패';
    }
    return '주소 생성 실패';
  }
}
